import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UiTheme, UiThemeInput } from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

const KEY = ['ui-themes'] as const;

/**
 * Assigning a theme happens on the Parameters page, whose `uiThemes` option
 * source is this same catalogue — so every write here invalidates both.
 */
function useThemeInvalidation() {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: KEY });
    await client.invalidateQueries({ queryKey: ['terminal-params'] });
  };
}

export function useUiThemes(enabled = true) {
  return useQuery({
    queryKey: KEY,
    enabled,
    queryFn: async () => (await apiClient.get<UiTheme[]>('/ui-themes')).data,
  });
}

export function useSaveUiTheme() {
  const invalidate = useThemeInvalidation();
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: UiThemeInput }) => {
      const response = id
        ? await apiClient.put<UiTheme>(`/ui-themes/${id}`, input)
        : await apiClient.post<UiTheme>('/ui-themes', input);
      return response.data;
    },
    onSuccess: invalidate,
  });
}

export function useArchiveUiTheme() {
  const invalidate = useThemeInvalidation();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/ui-themes/${id}`);
    },
    onSuccess: invalidate,
  });
}
